import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authApi, type SessionInfo } from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import {
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  LogOut,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  MapPin,
  Check,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { sv } from "date-fns/locale";

function getDeviceIcon(deviceName: string) {
  const name = deviceName.toLowerCase();
  if (name.includes("iphone") || name.includes("android") || name.includes("mobil")) {
    return Smartphone;
  }
  if (name.includes("ipad") || name.includes("tablet")) {
    return Tablet;
  }
  return Monitor;
}

function formatRelativeTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    return formatDistanceToNow(date, { addSuffix: true, locale: sv });
  } catch {
    return "Okänt";
  }
}

export function DeviceSessions() {
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const [showRevokeAllConfirm, setShowRevokeAllConfirm] = useState(false);

  // Get refresh token from localStorage
  const refreshToken = localStorage.getItem("kvanten_refresh_token") || undefined;

  // Fetch sessions
  const {
    data: sessions,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["sessions"],
    queryFn: async () => {
      const response = await authApi.getSessions(refreshToken);
      if (response.success && response.data) {
        return response.data.sessions;
      }
      throw new Error(response.error?.message || "Kunde inte hämta sessioner");
    },
  });

  // Revoke single session mutation
  const revokeMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await authApi.revokeSession(sessionId);
      if (!response.success) {
        throw new Error(response.error?.message || "Kunde inte avsluta sessionen");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      toast.success("Sessionen har avslutats");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Revoke all other sessions mutation
  const revokeAllMutation = useMutation({
    mutationFn: async () => {
      if (!refreshToken) {
        throw new Error("Ingen aktiv session");
      }
      const response = await authApi.revokeAllOtherSessions(refreshToken);
      if (!response.success) {
        throw new Error(response.error?.message || "Kunde inte avsluta sessioner");
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
      setShowRevokeAllConfirm(false);
      if (data?.revokedCount && data.revokedCount > 0) {
        toast.success(data.message);
      } else {
        toast.info("Inga andra sessioner att avsluta");
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">Kunde inte hämta sessioner</p>
          <button onClick={() => refetch()} className="btn btn-outline btn-sm">
            <RefreshCw size={16} />
            Försök igen
          </button>
        </div>
      </div>
    );
  }

  const currentSession = sessions?.find((s) => s.isCurrent);
  const otherSessions = sessions?.filter((s) => !s.isCurrent) || [];
  const displayedSessions = showAll ? otherSessions : otherSessions.slice(0, 3);
  const hasMoreSessions = otherSessions.length > 3;

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Aktiva sessioner</h2>
          <p className="text-sm text-gray-500 mt-1">
            Enheter där du är inloggad
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          title="Uppdatera"
        >
          <RefreshCw size={18} className="text-gray-500" />
        </button>
      </div>

      {/* Current session */}
      {currentSession && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Denna enhet</h3>
          <SessionCard session={currentSession} isCurrent />
        </div>
      )}

      {/* Other sessions */}
      {otherSessions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700">
              Andra enheter ({otherSessions.length})
            </h3>
            {otherSessions.length > 1 && (
              <button
                onClick={() => setShowRevokeAllConfirm(true)}
                className="text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Logga ut alla andra
              </button>
            )}
          </div>

          <div className="space-y-3">
            {displayedSessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onRevoke={() => revokeMutation.mutate(session.id)}
                isRevoking={revokeMutation.isPending}
              />
            ))}
          </div>

          {/* Show more/less button */}
          {hasMoreSessions && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="w-full mt-3 py-2 text-sm text-gray-600 hover:text-gray-900 flex items-center justify-center gap-1"
            >
              {showAll ? (
                <>
                  <ChevronUp size={16} />
                  Visa färre
                </>
              ) : (
                <>
                  <ChevronDown size={16} />
                  Visa alla ({otherSessions.length})
                </>
              )}
            </button>
          )}
        </div>
      )}

      {/* Empty state */}
      {otherSessions.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">
          Du är bara inloggad på denna enhet
        </p>
      )}

      {/* Revoke all confirmation */}
      {showRevokeAllConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Logga ut alla andra enheter?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Detta kommer att avsluta {otherSessions.length}{" "}
              {otherSessions.length === 1 ? "session" : "sessioner"} på andra enheter.
              Du förblir inloggad på denna enhet.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRevokeAllConfirm(false)}
                className="btn btn-outline flex-1"
                disabled={revokeAllMutation.isPending}
              >
                Avbryt
              </button>
              <button
                onClick={() => revokeAllMutation.mutate()}
                className="btn btn-danger flex-1"
                disabled={revokeAllMutation.isPending}
              >
                {revokeAllMutation.isPending ? (
                  <Spinner size="sm" />
                ) : (
                  <LogOut size={16} />
                )}
                Logga ut alla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SessionCard({
  session,
  isCurrent = false,
  onRevoke,
  isRevoking = false,
}: {
  session: SessionInfo;
  isCurrent?: boolean;
  onRevoke?: () => void;
  isRevoking?: boolean;
}) {
  const DeviceIcon = getDeviceIcon(session.deviceName);

  return (
    <div
      className={`flex items-start gap-4 p-4 rounded-lg border ${
        isCurrent
          ? "bg-primary-50 border-primary-200"
          : "bg-gray-50 border-gray-200"
      }`}
    >
      {/* Device icon */}
      <div
        className={`p-2 rounded-lg ${
          isCurrent ? "bg-primary-100 text-primary-600" : "bg-gray-200 text-gray-600"
        }`}
      >
        <DeviceIcon size={24} />
      </div>

      {/* Session info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900 truncate">
            {session.deviceName}
          </span>
          {isCurrent && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
              <Check size={12} />
              Denna enhet
            </span>
          )}
        </div>

        <div className="mt-1 space-y-1">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Globe size={14} />
              {session.browser} / {session.os}
            </span>
          </div>

          <div className="flex items-center gap-4 text-sm text-gray-500">
            {session.ipAddress && (
              <span className="flex items-center gap-1">
                <MapPin size={14} />
                {session.ipAddress}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {formatRelativeTime(session.createdAt)}
            </span>
          </div>
        </div>
      </div>

      {/* Revoke button */}
      {!isCurrent && onRevoke && (
        <button
          onClick={onRevoke}
          disabled={isRevoking}
          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
          title="Logga ut"
        >
          {isRevoking ? <Spinner size="sm" /> : <LogOut size={18} />}
        </button>
      )}
    </div>
  );
}
